import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { isIsBankasiMaximum } from '../../../core/utils/is-bankasi-bins.util';
import { CardBinService } from '../../../services/cardbin.service';

interface InstallmentOption {
  count: number;
  label: string;
}

const ALL_OPTIONS: ReadonlyArray<InstallmentOption> = [
  { count: 1, label: 'Peşin (Tek Çekim)' },
  { count: 2, label: '2 Taksit' },
  { count: 3, label: '3 Taksit' },
  { count: 6, label: '6 Taksit' },
  { count: 9, label: '9 Taksit' },
];

/**
 * İzole taksit seçici — OnPush + signals.
 * Kart numarası form control'ü üzerinden izlenir, BIN kontrolü backend API'ye yapılır.
 * API hata verirse (offline, 5xx) local BIN util'ine fallback.
 */
@Component({
  selector: 'app-installment-selector',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installment-selector.component.html',
  styleUrls: ['./installment-selector.component.css'],
})
export class InstallmentSelectorComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cardBinService = inject(CardBinService);

  readonly control = input.required<AbstractControl | null>();
  readonly maxInstallment = input<number>(9);
  readonly totalPrice = input<number>(0);
  readonly initialCount = input<number>(1);

  readonly installmentSelected = output<number>();

  readonly cardDigits = signal<string>('');
  readonly cardIsMaximum = signal<boolean>(false);
  readonly selectedCount = signal<number>(1);

  readonly show = computed(
    () => this.cardDigits().length >= 6 && this.cardIsMaximum() && this.maxInstallment() > 0
  );

  readonly hintText = computed<string | null>(() => {
    if (this.maxInstallment() <= 0) {
      return 'Yurt dışı oteller için taksit kullanılamaz.';
    }
    return null;
  });

  readonly options = computed<ReadonlyArray<InstallmentOption>>(() => {
    const max = this.maxInstallment();
    if (max <= 0) return [ALL_OPTIONS[0]];
    return ALL_OPTIONS.filter((opt) => opt.count === 1 || opt.count <= max);
  });

  constructor() {
    // Control değişince subscription kur — keystrokelar cardDigits signal'ını günceller (yalnızca digit, hafif)
    effect((onCleanup) => {
      const ctrl = this.control();
      if (!ctrl) return;
      this.cardDigits.set(((ctrl.value ?? '') as string).replace(/\D/g, ''));
      const sub = ctrl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value: string | null) => {
          this.cardDigits.set((value ?? '').replace(/\D/g, ''));
        });
      onCleanup(() => sub.unsubscribe());
    }, { allowSignalWrites: true });

    // cardDigits değişince backend'den BIN kontrolü — debounced, CD cycle dışında async
    effect((onCleanup) => {
      const digits = this.cardDigits();
      if (digits.length < 6) {
        this.cardIsMaximum.set(false);
        return;
      }
      // Debounce için manuel setTimeout (effect içinde RxJS pipe'a geçmek karmaşık)
      const handle = setTimeout(() => {
        const sub = this.cardBinService
          .check(digits)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (resp) => {
              // Backend bulduysa onu kullan, bulamadıysa local util'e fallback
              if (resp.found) {
                this.cardIsMaximum.set(resp.isInstallmentEligible);
              } else {
                this.cardIsMaximum.set(isIsBankasiMaximum(digits));
              }
            },
            error: () => {
              // Network hatası — fallback
              this.cardIsMaximum.set(isIsBankasiMaximum(digits));
            },
          });
        onCleanup(() => sub.unsubscribe());
      }, 300);
      onCleanup(() => clearTimeout(handle));
    }, { allowSignalWrites: true });

    // initialCount parent'tan değiştikçe lokal state güncelle
    effect(() => {
      this.selectedCount.set(this.initialCount());
    }, { allowSignalWrites: true });
  }

  selectInstallment(count: number): void {
    this.selectedCount.set(count);
    this.installmentSelected.emit(count);
  }
}
